import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  InputAdornment,
  Slider,
  Button,
  Tooltip,
  CircularProgress,
  Autocomplete,
  Paper,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import { getCountryFlag, cleanUrl, sortWebsitesByOrder } from '../../utils/countryFlags';
import TopGainersLosers from '../../components/rankings/TopGainersLosers';
import { KeywordManagementModal } from '../../components/rankings/KeywordManagementModal';
import { ContentUpdateModal } from '../../components/rankings/ContentUpdateModal';
import { getSupabaseClient } from '../../services/supabase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

interface CompetitorData {
  domain: string;
  position: number;
}

interface RankingData {
  keyword: string;
  position: number;
  previousPosition?: number;
  url_id: string;
  ranking_url?: string; // The actual URL that's ranking
  expected_domain?: string; // The domain we expected to rank
  is_alternative_domain?: boolean; // Flag if it's a different domain than expected
  check_date: string;
  clicks?: number;
  impressions?: number;
  ctr?: number;
  competitors?: CompetitorData[];
}

interface CompetitorRanking {
  domain: string;
  position: number;
  check_date: string;
}

interface MetricsData {
  totalKeywords: number;
  averagePosition: number;
  keywordsInTop10: number;
  keywordsInTop3: number;
}

const RankingsPage: React.FC = () => {
  const [rankings, setRankings] = useState<RankingData[]>([]);
  const [filteredRankings, setFilteredRankings] = useState<RankingData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [metrics, setMetrics] = useState<MetricsData>({
    totalKeywords: 0,
    averagePosition: 0,
    keywordsInTop10: 0,
    keywordsInTop3: 0
  });

  // Website selector and filter states (same as dashboard)
  const [websites, setWebsites] = useState<any[]>([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string>('all');
  const [selectedContentType, setSelectedContentType] = useState<string>('all');
  
  // Filter states - default to month-to-date
  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const firstDay = `${year}-${month}-01`;
    const today = String(now.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${today}`;
    return { fromDate: firstDay, toDate: todayStr };
  };
  
  const [dateFilter, setDateFilter] = useState(getCurrentMonthRange());
  const [urlFilter, setUrlFilter] = useState<string>('');
  const [keywordFilter, setKeywordFilter] = useState<string>('');
  const [availableUrls, setAvailableUrls] = useState<string[]>([]);
  const [availableKeywords, setAvailableKeywords] = useState<string[]>([]);
  
  // Rankings-specific filters
  const [searchTerm, setSearchTerm] = useState('');
  const [positionRange, setPositionRange] = useState<number[]>([1, 100]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Side Panel for keyword details
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [selectedKeywordForDetails, setSelectedKeywordForDetails] = useState<{
    keyword: string;
    urlId: string;
    position: number;
    url?: string;
    searchVolume: number;
    estimatedVisits: number;
    clicks?: number;
    impressions?: number;
    ctr?: number;
    competitors?: CompetitorData[];
    alternativeUrls?: Array<{
      url_id: string;
      url: string;
      position: number;
      website_id: string;
    }>;
  } | null>(null);
  const [positionHistory, setPositionHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [contentUpdates, setContentUpdates] = useState<any[]>([]);
  const [isLoadingUpdates, setIsLoadingUpdates] = useState(false);
  
  // Keyword Management Modal
  const [keywordManagementOpen, setKeywordManagementOpen] = useState(false);
  const [selectedUrlIdForManagement, setSelectedUrlIdForManagement] = useState<string | null>(null);
  
  // Content Update Modal
  const [contentUpdateOpen, setContentUpdateOpen] = useState(false);
  const [selectedUpdateData, setSelectedUpdateData] = useState<{
    urlId: string;
    urlPath: string;
    currentPosition: number;
  } | null>(null);

  const contentTypes = [
    { value: 'all', label: 'All Content' },
    { value: 'V', label: 'VIP' },
    { value: 'S', label: 'SEO' },
    { value: 'C', label: 'Commercial' },
    { value: 'I', label: 'Informational' },
    { value: 'IC', label: 'Info-Commercial' }
  ];

  // Fetch available websites (EXACT same as dashboard)
  const fetchWebsites = async () => {
    try {
      const supabaseAdmin = await getSupabaseClient(true);
      
      const { data: websiteData, error: websiteError } = await getSupabaseClient(true)
        .from('websites')
        .select('website_id, country, base_url')
        .neq('country', 'GLOBAL'); // Exclude the GLOBAL entry
      
      if (websiteError) throw websiteError;
      
      if (websiteData) {
        // Sort websites using the utility function
        const sortedWebsites = sortWebsitesByOrder(websiteData);
        setWebsites(sortedWebsites);
        console.log('Available websites:', sortedWebsites);
      }
    } catch (error) {
      console.error('Error fetching websites:', error);
    }
  };

  const handleWebsiteChange = (event: any) => {
    setSelectedWebsiteId(event.target.value);
  };

  const handleDateRangeChange = (dateRange: { fromDate: string; toDate: string }) => {
    setDateFilter(dateRange);
  };

  const handleClearFilters = () => {
    setUrlFilter('');
    setKeywordFilter('');
    setSelectedContentType('all');
    setSearchTerm('');
    setPositionRange([1, 100]);
  };

  const handleOpenKeywordManagement = () => {
    if (!urlFilter) return;
    
    // Get the URL ID from the selected URL
    const matchingUrl = availableUrls.find(url => url === urlFilter);
    if (matchingUrl) {
      // Fetch the actual url_id from the database
      fetchUrlId(matchingUrl);
    }
  };

  const fetchUrlId = async (fullUrl: string) => {
    try {
      const supabase = await getSupabaseClient(true);
      const { data, error } = await supabase
        .from('urls')
        .select('url_id')
        .eq('full_url', fullUrl)
        .single();
      
      if (error) throw error;
      
      if (data) {
        setSelectedUrlIdForManagement(data.url_id);
        setKeywordManagementOpen(true);
      }
    } catch (error) {
      console.error('Error fetching URL ID:', error);
      alert('Error loading URL data. Please try again.');
    }
  };

  const handleOpenContentUpdate = (urlId: string, urlPath: string | undefined, position: number) => {
    setSelectedUpdateData({
      urlId,
      urlPath: urlPath || '',
      currentPosition: position
    });
    setContentUpdateOpen(true);
  };

  useEffect(() => {
    fetchWebsites();
  }, []);

  useEffect(() => {
    if (selectedWebsiteId !== 'all') {
      fetchRankings(0, 25, true); // Start with just 25 records
    }
  }, [selectedWebsiteId, dateFilter, selectedContentType, urlFilter, keywordFilter]);
  
  // Separate useEffect for filter options - only when website changes
  useEffect(() => {
    if (selectedWebsiteId !== 'all') {
      fetchFilterOptions();
    }
  }, [selectedWebsiteId]);

  useEffect(() => {
    applyFilters();
  }, [rankings, searchTerm, positionRange]);

  // Fetch rankings data with progressive loading
  const fetchRankings = async (offset = 0, limit = 25, isInitialLoad = true) => {
    if (selectedWebsiteId === 'all') {
      setRankings([]);
      setMetrics({ totalKeywords: 0, averagePosition: 0, keywordsInTop10: 0, keywordsInTop3: 0 });
      return;
    }

    if (isInitialLoad) {
      setLoading(true);
      setRankings([]); // Clear previous data
    } else {
      setIsLoadingMore(true);
    }
    
    try {
      const supabaseAdmin = await getSupabaseClient(true);

      // Get rankings data from serp_rankings table (SAME as dashboard)
      let rankingsQuery = supabaseAdmin
        .from('serp_rankings')
        .select('ranking, keyword, check_date, url_id')
        .gte('check_date', dateFilter.fromDate)
        .lte('check_date', dateFilter.toDate)
        .order('check_date', { ascending: false });
        
      // Find the selected website to get its country code (SAME as dashboard)
      const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
      if (selectedWebsite) {
        console.log(`🌐 Selected website:`, selectedWebsite);
        // Filter by URL pattern using country code (SAME as dashboard)
        const websiteFilter = `${selectedWebsite.country}-%`;
        console.log(`🔍 Website filter pattern: "${websiteFilter}"`);
        rankingsQuery = rankingsQuery.like('url_id', websiteFilter);
        
        // Filter by content type if one is selected (SAME as dashboard)
        if (selectedContentType !== 'all') {
          rankingsQuery = rankingsQuery.like('url_id', `%-${selectedContentType}%`);
        }
        
        // Apply URL filter if specified
        if (urlFilter) {
          // Need to convert full URL back to url_id
          const { data: matchingUrls } = await getSupabaseClient(true)
            .from('urls')
            .select('url_id')
            .eq('full_url', urlFilter);
          
          if (matchingUrls && matchingUrls.length > 0) {
            rankingsQuery = rankingsQuery.eq('url_id', matchingUrls[0].url_id);
          }
        }
        
        // Apply keyword filter if specified (SAME as dashboard)
        if (keywordFilter) {
          rankingsQuery = rankingsQuery.ilike('keyword', `%${keywordFilter}%`);
        }
      } else {
        throw new Error('Selected website not found');
      }
      
      const { data: rankingsData, error: rankingsError } = await rankingsQuery
        .range(offset, offset + limit - 1)
        .order('check_date', { ascending: false });

      if (rankingsError) throw rankingsError;

      // For initial load, only fetch URLs for the rankings we have
      const urlMap = new Map();
      if (isInitialLoad && rankingsData && rankingsData.length > 0) {
        const urlIds = Array.from(new Set(rankingsData.map((r: any) => r.url_id)));
        const { data: urlsData, error: urlsError } = await getSupabaseClient(true)
          .from('urls')
          .select('url_id, full_url')
          .in('url_id', urlIds); // Only fetch URLs we need
        
        if (urlsError) {
          console.error('Error fetching URLs:', urlsError);
        }
        
        urlsData?.forEach(url => {
          urlMap.set(url.url_id, url.full_url);
        });
        console.log(`📍 URL mapping: ${urlMap.size} URLs loaded for ${urlIds.length} rankings`);
      } else if (!isInitialLoad) {
        // For background loads, fetch all URLs
        const { data: urlsData, error: urlsError } = await getSupabaseClient(true)
          .from('urls')
          .select('url_id, full_url')
          .eq('website_id', selectedWebsite.website_id);
        
        urlsData?.forEach(url => {
          urlMap.set(url.url_id, url.full_url);
        });
      }

      if (rankingsData && rankingsData.length > 0) {
        console.log(`🔍 Found ${rankingsData.length} total rankings`);
        
        // Get all unique keywords for batch fetching
        const uniqueKeywords = Array.from(new Set(rankingsData.map((item: any) => item.keyword)));
        console.log(`📊 Processing ${uniqueKeywords.length} unique keywords...`);
        
        // Skip competitor data on initial load for speed
        let competitorMap: Map<string, CompetitorData[]> = new Map();
        if (!isInitialLoad) {
          try {
            const { data: allCompetitorData, error: competitorError } = await (supabaseAdmin as any)
              .from('serp_competitors')
              .select('keyword, competitor_position, competitor_url, competitor_title, check_date')
              .in('keyword', uniqueKeywords)
              .gte('check_date', dateFilter.fromDate)
              .lte('check_date', dateFilter.toDate)
              .order('competitor_position', { ascending: true });

          if (competitorError) {
            console.log(`❌ Batch competitor query error:`, competitorError);
          } else if (allCompetitorData && allCompetitorData.length > 0) {
            // Group competitors by keyword
            allCompetitorData.forEach((comp: any) => {
              const keyword = comp.keyword;
              if (!competitorMap.has(keyword)) {
                competitorMap.set(keyword, []);
              }
              
              // Extract domain from competitor_url
              let domain = 'Unknown';
              if (comp.competitor_url) {
                try {
                  const url = new URL(comp.competitor_url);
                  domain = url.hostname.replace('www.', '');
                } catch {
                  domain = comp.competitor_url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
                }
              }
              
              const competitors = competitorMap.get(keyword)!;
              if (competitors.length < 3) { // Limit to 3 competitors per keyword
                competitors.push({
                  domain: domain,
                  position: comp.competitor_position || 999
                });
              }
            });
            console.log(`🏆 Found competitors for ${competitorMap.size} keywords`);
          }
          } catch (err) {
            console.log(`💥 Batch competitor query exception:`, err);
          }
        }
        
        // Fetch previous period data for trend comparison
        const prevPositionMap: Map<string, number> = new Map();
        try {
          // Calculate previous period based on selected date range
          const fromDate = new Date(dateFilter.fromDate);
          const toDate = new Date(dateFilter.toDate);
          const rangeDays = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
          
          // Get data from the previous period of the same length
          const prevFromDate = new Date(fromDate);
          prevFromDate.setDate(prevFromDate.getDate() - rangeDays - 1);
          const prevToDate = new Date(fromDate);
          prevToDate.setDate(prevToDate.getDate() - 1);
          
          const { data: prevData } = await getSupabaseClient(true)
            .from('serp_rankings')
            .select('keyword, ranking')
            .in('keyword', uniqueKeywords)
            .like('url_id', `${selectedWebsite.country}-%`)
            .gte('check_date', prevFromDate.toISOString().split('T')[0])
            .lte('check_date', prevToDate.toISOString().split('T')[0])
            .order('check_date', { ascending: false });
            
          if (prevData) {
            // Group by keyword and keep only the most recent previous position
            const keywordPrevMap: Map<string, number> = new Map();
            prevData.forEach((item: any) => {
              if (!keywordPrevMap.has(item.keyword)) {
                keywordPrevMap.set(item.keyword, item.ranking);
              }
            });
            prevPositionMap.clear();
            keywordPrevMap.forEach((ranking, keyword) => {
              prevPositionMap.set(keyword, ranking);
            });
          }
        } catch (err) {
          console.log(`💥 Previous position query exception:`, err);
        }
        
        // Skip Search Console data on initial load
        const searchConsoleMap: Map<string, {clicks: number, impressions: number, ctr: number}> = new Map();
        if (!isInitialLoad) {
          try {
            const { data: searchConsoleData, error: searchConsoleError } = await getSupabaseClient(true)
              .from('search_console_keywords')
              .select('query, clicks, impressions, ctr')
              .in('query', uniqueKeywords)  // Match against actual search queries
              .gte('date_retrieved', dateFilter.fromDate)
              .lte('date_retrieved', dateFilter.toDate);

          if (searchConsoleError) {
            console.log('❌ Search Console query error:', searchConsoleError);
          } else if (searchConsoleData && searchConsoleData.length > 0) {
            // Aggregate Search Console data by query (which maps to our keywords)
            searchConsoleData.forEach((item: any) => {
              const query = item.query;
              const existing = searchConsoleMap.get(query) || { clicks: 0, impressions: 0, ctr: 0 };
              searchConsoleMap.set(query, {
                clicks: existing.clicks + (item.clicks || 0),
                impressions: existing.impressions + (item.impressions || 0),
                ctr: existing.impressions > 0 
                  ? ((existing.clicks + (item.clicks || 0)) / (existing.impressions + (item.impressions || 0))) * 100
                  : 0
              });
            });
            console.log(`📊 Found Search Console data for ${searchConsoleMap.size} queries`);
          }
          } catch (err) {
            console.log('💥 Search Console query exception:', err);
          }
        }
        
        // Process rankings data without individual async calls
        const processedRankings = rankingsData.map((item: any) => {
          // Extract domain from url_id to check if it matches expected website
          const urlIdDomain = item.url_id ? item.url_id.split('-')[0] : '';
          const expectedDomain = selectedWebsite.base_url.replace(/^https?:\/\//, '').replace(/^www\./, '');
          const isAlternativeDomain = urlIdDomain.toLowerCase() !== selectedWebsite.country.toLowerCase();
          
          // Get Search Console data for this keyword (matched by query), or use defaults
          const searchConsoleMetrics = searchConsoleMap.get(item.keyword) || {
            clicks: 0,
            impressions: 0,
            ctr: 0
          };
          
          // Get URL from mapping
          const fullUrl = urlMap.get(item.url_id);
          if (!fullUrl && item.url_id) {
            console.warn(`⚠️ No URL found for: ${item.url_id} urlInfo:`, item.url, `RankingsPage.tsx:${new Error().stack?.split('\n')[2]?.match(/:(\d+):/)?.[1] || 'unknown'}`);
          }
          
          return {
            keyword: item.keyword,
            position: item.ranking,
            previousPosition: prevPositionMap.get(item.keyword),
            url_id: item.url_id,
            ranking_url: fullUrl || '', // Use actual URL from mapping
            expected_domain: expectedDomain,
            is_alternative_domain: isAlternativeDomain,
            check_date: item.check_date,
            clicks: searchConsoleMetrics.clicks,
            impressions: searchConsoleMetrics.impressions,
            ctr: searchConsoleMetrics.ctr,
            competitors: competitorMap.get(item.keyword) || []
          };
        });

        // Deduplicate by keyword, keeping only the latest ranking (SAME as dashboard logic)
        const keywordMap = new Map();
        processedRankings.forEach(item => {
          if (!keywordMap.has(item.keyword)) {
            keywordMap.set(item.keyword, item);
          }
        });

        const uniqueRankings = Array.from(keywordMap.values());
        console.log(`🎯 Unique keywords loaded:`, uniqueRankings.length);
        console.log(`📝 Sample keywords:`, uniqueRankings.slice(0, 10).map(r => r.keyword));
        
        // Check if specific keywords exist
        const heliKeywords = uniqueRankings.filter(r => r.keyword.toLowerCase().includes('heli'));
        const knicksKeywords = uniqueRankings.filter(r => r.keyword.toLowerCase().includes('knick'));
        console.log(`🔍 "heli" keywords found:`, heliKeywords.length, heliKeywords.map(r => r.keyword));
        console.log(`🔍 "knick" keywords found:`, knicksKeywords.length, knicksKeywords.map(r => r.keyword));
        
        if (isInitialLoad) {
          setRankings(uniqueRankings);
          calculateMetrics(uniqueRankings);
          
          // If we got full limit, there might be more data
          if (rankingsData.length === limit) {
            setHasMoreData(true);
            // Load remaining data in background immediately
            setTimeout(() => {
              fetchRankings(offset + limit, 2000, false);
            }, 10); // Reduced from 100ms to 10ms
          } else {
            setHasMoreData(false);
          }
        } else {
          // Append to existing data
          setRankings(prev => {
            const combined = [...prev, ...uniqueRankings];
            // Deduplicate again in case of overlaps
            const finalMap = new Map();
            combined.forEach(item => {
              if (!finalMap.has(item.keyword)) {
                finalMap.set(item.keyword, item);
              }
            });
            const finalRankings = Array.from(finalMap.values());
            calculateMetrics(finalRankings);
            return finalRankings;
          });
          setHasMoreData(rankingsData.length === limit);
        }
      } else {
        if (isInitialLoad) {
          setRankings([]);
          setMetrics({ totalKeywords: 0, averagePosition: 0, keywordsInTop10: 0, keywordsInTop3: 0 });
        }
        setHasMoreData(false);
      }
      
    } catch (error) {
      console.error('Error fetching rankings:', error);
      if (isInitialLoad) {
        setRankings([]);
        setMetrics({ totalKeywords: 0, averagePosition: 0, keywordsInTop10: 0, keywordsInTop3: 0 });
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  // Fetch ALL URLs from the urls table for the selected website
  const fetchFilterOptions = async () => {
    console.log('🚀 fetchFilterOptions called for website:', selectedWebsiteId);
    try {
      const supabaseAdmin = await getSupabaseClient(true);
      
      // Get ALL URLs from the urls table for this website
      let urlQuery = supabaseAdmin
        .from('urls')
        .select('url_id, full_url')
        .order('full_url');
        
      // Filter by website
      if (selectedWebsiteId !== 'all') {
        urlQuery = urlQuery.eq('website_id', selectedWebsiteId);
      }
      
      const { data: urlResult, error: urlError } = await urlQuery;
      
      if (urlError) {
        console.error('❌ Error fetching URLs:', urlError);
      } else if (!urlResult || urlResult.length === 0) {
        console.warn('⚠️ No URLs found for website:', selectedWebsiteId);
        setAvailableUrls([]);
      } else {
        // Extract ALL URLs from the urls table
        console.log('✅ Fetched', urlResult.length, 'URLs from database');
        const urls = urlResult.map(item => item.full_url).filter(Boolean);
        console.log('📋 Setting', urls.length, 'URLs in dropdown');
        console.log('📋 Sample URLs:', urls.slice(0, 5));
        setAvailableUrls(urls);
      }
      
      // For keywords, we can keep the existing approach or simplify similarly
      if (selectedWebsiteId !== 'all') {
        const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
        if (selectedWebsite) {
          const { data: keywordData } = await supabaseAdmin
            .from('serp_rankings')
            .select('keyword')
            .like('url_id', `${selectedWebsite.country}-%`)
            .limit(1000);
            
          if (keywordData) {
            const keywords = Array.from(new Set(keywordData.map(item => item.keyword))).sort();
            setAvailableKeywords(keywords);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error in fetchFilterOptions:', error);
    }
  };

  const calculateMetrics = (rankingsData: RankingData[]) => {
    const totalKeywords = rankingsData.length;
    const averagePosition = totalKeywords > 0 
      ? rankingsData.reduce((sum, item) => sum + item.position, 0) / totalKeywords 
      : 0;
    const keywordsInTop10 = rankingsData.filter(item => item.position <= 10).length;
    const keywordsInTop3 = rankingsData.filter(item => item.position <= 3).length;

    setMetrics({
      totalKeywords,
      averagePosition: Math.round(averagePosition * 10) / 10,
      keywordsInTop10,
      keywordsInTop3
    });
  };

  const applyFilters = () => {
    let filtered = rankings;
    console.log(`🔍 Starting filter with ${rankings.length} rankings`);

    // Search filter
    if (searchTerm) {
      const beforeFilter = filtered.length;
      filtered = filtered.filter(item =>
        item.keyword.toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log(`🔍 Search "${searchTerm}": ${beforeFilter} -> ${filtered.length} results`);
      console.log(`🔍 Matching keywords:`, filtered.map(r => r.keyword));
    }

    // Position range filter
    const beforeRangeFilter = filtered.length;
    filtered = filtered.filter(item =>
      item.position >= positionRange[0] && item.position <= positionRange[1]
    );
    console.log(`🔍 Position range ${positionRange[0]}-${positionRange[1]}: ${beforeRangeFilter} -> ${filtered.length} results`);

    setFilteredRankings(filtered);
    setPage(0);
  };

  const getTrendIcon = (current: number, previous?: number) => {
    if (!previous) return (
      <Tooltip title="Comparing to previous period">
        <RemoveIcon sx={{ fontSize: 16, color: 'grey.500' }} />
      </Tooltip>
    );
    if (current < previous) return (
      <Tooltip title={`Was #${previous} in previous period`}>
        <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
      </Tooltip>
    );
    if (current > previous) return (
      <Tooltip title={`Was #${previous} in previous period`}>
        <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
      </Tooltip>
    );
    return (
      <Tooltip title="No change from previous period">
        <RemoveIcon sx={{ fontSize: 16, color: 'grey.500' }} />
      </Tooltip>
    );
  };

  const getPositionColor = (position: number) => {
    if (position <= 3) return 'success';
    if (position <= 10) return 'warning';
    return 'error';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCTR = (ctr: number) => {
    return `${ctr.toFixed(1)}%`;
  };

  const paginatedRankings = filteredRankings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Fetch position history for a specific keyword and URL
  const fetchPositionHistory = async (keyword: string, urlId: string): Promise<any[]> => {
    try {
      const supabaseAdmin = await getSupabaseClient(true);
      
      // Get position history within the selected date range
      const { data: historyData, error } = await getSupabaseClient(true)
        .from('serp_rankings')
        .select('ranking, check_date')
        .eq('keyword', keyword)
        .eq('url_id', urlId)
        .gte('check_date', dateFilter.fromDate)
        .lte('check_date', dateFilter.toDate)
        .order('check_date', { ascending: true });

      if (error) {
        console.error('Error fetching position history:', error);
        return [];
      }

      return historyData || [];
    } catch (error) {
      console.error('Position history fetch error:', error);
      return [];
    }
  };

  // Fetch alternative URLs ranking for the same keyword
  const fetchAlternativeUrls = async (keyword: string): Promise<any[]> => {
    try {
      const supabaseAdmin = await getSupabaseClient(true);
      
      // Get all URLs ranking for this keyword
      const { data: rankingsData, error: rankingsError } = await getSupabaseClient(true)
        .from('serp_rankings')
        .select('url_id, ranking, check_date')
        .eq('keyword', keyword)
        .order('check_date', { ascending: false });

      if (rankingsError) {
        console.error('Error fetching alternative rankings:', rankingsError);
        return [];
      }

      if (!rankingsData || rankingsData.length === 0) return [];

      // Get latest ranking for each URL
      const latestByUrl = new Map();
      rankingsData.forEach(item => {
        if (!latestByUrl.has(item.url_id)) {
          latestByUrl.set(item.url_id, item);
        }
      });

      const urlIds = Array.from(latestByUrl.keys());
      
      // Get URL details
      const { data: urlsData, error: urlsError } = await getSupabaseClient(true)
        .from('urls')
        .select('url_id, website_id, full_url')
        .in('url_id', urlIds);

      if (urlsError) {
        console.error('Error fetching alternative URLs:', urlsError);
        return [];
      }

      // Combine ranking and URL data
      const alternatives = urlsData?.map(url => {
        const ranking = latestByUrl.get(url.url_id);
        return {
          url_id: url.url_id,
          url: url.full_url,
          position: ranking?.ranking || 999,
          website_id: url.website_id
        };
      }).sort((a, b) => a.position - b.position) || [];

      return alternatives;
    } catch (error) {
      console.error('Alternative URLs fetch error:', error);
      return [];
    }
  };

  const handleOpenSidePanel = async (
    keyword: string, 
    urlId: string, 
    position: number, 
    url: string | undefined,
    searchVolume: number,
    estimatedVisits: number,
    clicks?: number,
    impressions?: number,
    ctr?: number,
    competitors?: CompetitorData[]
  ) => {
    setSelectedKeywordForDetails({ 
      keyword, 
      urlId, 
      position, 
      url,
      searchVolume,
      estimatedVisits,
      clicks,
      impressions,
      ctr,
      competitors 
    });
    setSidePanelOpen(true);
    
    // Fetch position history and content updates for this keyword/URL
    setIsLoadingHistory(true);
    setIsLoadingUpdates(true);
    try {
      const [history, alternativeUrls] = await Promise.all([
        fetchPositionHistory(keyword, urlId),
        fetchAlternativeUrls(keyword)
      ]);
      
      setPositionHistory(history);
      
      // Update selected keyword details with alternative URLs
      setSelectedKeywordForDetails(prev => prev ? {
        ...prev,
        alternativeUrls
      } : null);
      
      // Fetch content updates for this URL
      const { data: updates, error: updatesError } = await (getSupabaseClient(true) as any)
        .from('content_updates')
        .select('*')
        .eq('url_id', urlId)
        .order('update_date', { ascending: false });
      
      if (!updatesError && updates) {
        setContentUpdates(updates);
      } else {
        setContentUpdates([]);
      }
      
    } catch (error) {
      console.error('Error loading keyword details:', error);
      setPositionHistory([]);
    } finally {
      setIsLoadingHistory(false);
      setIsLoadingUpdates(false);
    }
  };

  const handleCloseSidePanel = () => {
    setSidePanelOpen(false);
    setSelectedKeywordForDetails(null);
    setPositionHistory([]);
    setIsLoadingHistory(false);
    setContentUpdates([]);
    setIsLoadingUpdates(false);
  };

  const getPositionColorForPanel = (position: number) => {
    if (position <= 3) return '#4CAF50';
    if (position <= 10) return '#FF9800';
    if (position <= 20) return '#2196F3';
    return '#F44336';
  };

  const estimateTraffic = (position: number, searchVolume: number) => {
    // Real CTR rates from actual SERP data (2024/2025)
    const ctrByPosition: { [key: number]: number } = {
      1: 0.3014,  // 30.14%
      2: 0.1850,  // 18.50%
      3: 0.1124,  // 11.24%
      4: 0.0718,  // 7.18%
      5: 0.0498,  // 4.98%
      6: 0.0359,  // 3.59%
      7: 0.0259,  // 2.59%
      8: 0.0196,  // 1.96%
      9: 0.0150,  // 1.50%
      10: 0.0118  // 1.18%
    };
    
    let ctr = 0.005; // Default for positions beyond 10
    if (position <= 10) {
      ctr = ctrByPosition[position] || 0.005;
    } else if (position <= 20) {
      ctr = 0.008; // ~0.8% for positions 11-20
    } else if (position <= 50) {
      ctr = 0.003; // ~0.3% for positions 21-50
    }
    
    return Math.round(searchVolume * ctr);
  };

  const generateSearchVolume = (keyword: string) => {
    // Mock search volume based on keyword characteristics
    const baseVolumes: { [key: string]: number } = {
      'new york': 12000,
      'things to buy': 5000,
      'shopping': 8000,
      'free things': 3000,
      '9 11 memorial': 1500,
      'ground zero': 2000
    };
    
    // Find matching base volume or generate based on keyword length/type
    for (const [key, volume] of Object.entries(baseVolumes)) {
      if (keyword.toLowerCase().includes(key)) {
        return volume + Math.floor(Math.random() * 1000);
      }
    }
    
    // Default based on keyword characteristics
    if (keyword.length > 25) return Math.floor(Math.random() * 500 + 100);
    if (keyword.includes('tickets')) return Math.floor(Math.random() * 3000 + 1000);
    return Math.floor(Math.random() * 2000 + 500);
  };

  // Create position history chart data - only use real data
  const createPositionChartData = () => {
    return positionHistory || [];
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          Rankings
        </Typography>

        {selectedWebsiteId === 'all' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Select a website below to view SEO rankings data
            </Typography>
          </Box>
        )}

        {/* Website Selector (SAME as dashboard) */}
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="website-select-label">Select Website</InputLabel>
            <Select
              labelId="website-select-label"
              value={selectedWebsiteId}
              label="Select Website"
              onChange={handleWebsiteChange}
            >
              <MenuItem value="all">All Websites</MenuItem>
              {websites.map((website) => (
                <MenuItem key={website.website_id} value={website.website_id}>
                  {getCountryFlag(website.country)} {cleanUrl(website.base_url)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Advanced Filters (SAME structure as dashboard) */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: '#666' }} />
            <Typography variant="h6" sx={{ color: '#333', fontWeight: 600, flexGrow: 1 }}>
              Filters
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
              sx={{ 
                height: '40px',
                borderRadius: '4px',
                textTransform: 'none',
                color: '#666',
                borderColor: '#eaeaea',
                '&:hover': {
                  borderColor: '#ddd',
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Clear Filters
            </Button>
          </Box>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DateRangeSelector
                fromDate={dateFilter.fromDate}
                toDate={dateFilter.toDate}
                onChange={handleDateRangeChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={availableUrls}
                value={urlFilter}
                onChange={(_, newValue) => setUrlFilter(newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by URL"
                    size="small"
                    placeholder="Select or type URL..."
                  />
                )}
                freeSolo
                clearOnEscape
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Autocomplete
                  options={availableKeywords}
                  value={keywordFilter}
                  onChange={(_, newValue) => setKeywordFilter(newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Keyword"
                      size="small"
                      placeholder="Select or type keyword..."
                    />
                  )}
                  freeSolo
                  clearOnEscape
                  sx={{ flex: 1 }}
                />
                {urlFilter && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenKeywordManagement}
                    startIcon={<SettingsIcon />}
                    sx={{ 
                      height: '40px',
                      minWidth: 'auto',
                      px: 1,
                      borderColor: '#7b61ff',
                      color: '#7b61ff',
                      '&:hover': {
                        borderColor: '#6b51e0',
                        bgcolor: '#f3f0ff'
                      }
                    }}
                    title="Manage Keywords"
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Manage</Box>
                  </Button>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={selectedContentType}
                  label="Content Type"
                  onChange={(e) => setSelectedContentType(e.target.value)}
                >
                  {contentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {selectedWebsiteId !== 'all' && (
          <>
            {/* Metrics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Keywords
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                    {formatNumber(metrics.totalKeywords)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Average Position
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                    {metrics.averagePosition}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Keywords in Top 10
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                    {formatNumber(metrics.keywordsInTop10)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Keywords in Top 3
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                    {formatNumber(metrics.keywordsInTop3)}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Top Gainers/Losers Widget */}
            {(() => {
              const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
              console.log('RankingsPage - widget check:', { 
                selectedWebsiteId, 
                selectedWebsite,
                country: selectedWebsite?.country,
                websitesCount: websites.length 
              });
              return selectedWebsiteId !== 'all' && selectedWebsite && (
                <Box sx={{ mb: 3 }}>
                  <TopGainersLosers 
                    websiteId={selectedWebsiteId} 
                    country={selectedWebsite.country}
                    dateRange={{ from: dateFilter.fromDate, to: dateFilter.toDate }}
                  />
                </Box>
              );
            })()}

            {/* Rankings-specific Filters */}
            <Card sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Rankings Filters
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Search Keywords"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>
                    Position Range: {positionRange[0]} - {positionRange[1]}
                  </Typography>
                  <Slider
                    value={positionRange}
                    onChange={(_, newValue) => setPositionRange(newValue as number[])}
                    valueLabelDisplay="auto"
                    min={1}
                    max={100}
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Rankings Table */}
            <Card>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Keyword</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Trend</TableCell>
                      <TableCell>URL</TableCell>
                      <TableCell>Competitors</TableCell>
                      <TableCell>Clicks*</TableCell>
                      <TableCell>Impressions*</TableCell>
                      <TableCell>CTR*</TableCell>
                      <TableCell width={50}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : paginatedRankings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No rankings found for the selected filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRankings.map((ranking, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Tooltip title={`Last checked: ${new Date(ranking.check_date).toLocaleDateString()}`} arrow>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 500, 
                                  cursor: 'pointer',
                                  '&:hover': { color: '#7b61ff', textDecoration: 'underline' }
                                }}
                                onClick={() => {
                                  const searchVolume = generateSearchVolume(ranking.keyword);
                                  const estimatedVisits = estimateTraffic(ranking.position, searchVolume);
                                  handleOpenSidePanel(
                                    ranking.keyword,
                                    ranking.url_id,
                                    ranking.position,
                                    ranking.ranking_url,
                                    searchVolume,
                                    estimatedVisits,
                                    ranking.clicks,
                                    ranking.impressions,
                                    ranking.ctr,
                                    ranking.competitors
                                  );
                                }}
                              >
                                {ranking.keyword}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={ranking.position}
                              color={getPositionColor(ranking.position)}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            {getTrendIcon(ranking.position, ranking.previousPosition)}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={ranking.ranking_url || 'URL mapping not found in database'}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {ranking.is_alternative_domain && (
                                  <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                )}
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: ranking.is_alternative_domain ? 'warning.main' : 'text.secondary',
                                    fontWeight: ranking.is_alternative_domain ? 500 : 400,
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {ranking.ranking_url ? 
                                    ranking.ranking_url.replace(/^https?:\/\/[^\/]+/, '').slice(0, 40) + (ranking.ranking_url.length > 40 ? '...' : '') 
                                    : 'URL not found'}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ minWidth: 120 }}>
                              {ranking.competitors && ranking.competitors.length > 0 ? (
                                ranking.competitors.slice(0, 2).map((competitor, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ 
                                      color: competitor.position < ranking.position ? 'error.main' : 'success.main',
                                      fontWeight: 500,
                                      minWidth: 20 
                                    }}>
                                      #{competitor.position}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: '0.7rem' }}>
                                      {competitor.domain.replace(/^www\./, '').substring(0, 12)}
                                    </Typography>
                                    {competitor.position < ranking.position && (
                                      <Tooltip title={`${competitor.domain} is beating us`}>
                                        <TrendingUpIcon sx={{ fontSize: 12, color: 'error.main', ml: 0.5 }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {ranking.position === 1 ? 'No competitors' : 'Not checked yet'}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: (ranking.clicks || 0) > 0 ? 'inherit' : '#999' }}>
                              {(ranking.clicks || 0) > 0 ? formatNumber(ranking.clicks!) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: (ranking.impressions || 0) > 0 ? 'inherit' : '#999' }}>
                              {(ranking.impressions || 0) > 0 ? formatNumber(ranking.impressions!) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: (ranking.ctr || 0) > 0 ? 'inherit' : '#999' }}>
                              {(ranking.ctr || 0) > 0 ? formatCTR(ranking.ctr!) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenContentUpdate(ranking.url_id, ranking.ranking_url, ranking.position)}
                              sx={{ 
                                color: '#7b61ff',
                                '&:hover': {
                                  bgcolor: 'rgba(123, 97, 255, 0.08)'
                                }
                              }}
                              title="Log content update"
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={filteredRankings.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
              <Box sx={{ p: 2, borderTop: '1px solid #f0f0f0', bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary">
                  * Search Console data (clicks, impressions, CTR) shows actual user queries that match your tracked keywords.
                  Competitor data is fetched from the serp_competitors table.
                </Typography>
                {isLoadingMore && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Loading trends, competitors, and remaining keywords in background...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </>
        )}
      </Box>
      
      {/* Side Panel */}
      {sidePanelOpen && selectedKeywordForDetails && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: { xs: '100vw', md: '400px' },
          height: '100vh',
          bgcolor: 'white',
          borderLeft: '1px solid #e0e0e0',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
          overflow: 'auto',
          transition: 'transform 0.3s ease',
          transform: sidePanelOpen ? 'translateX(0)' : 'translateX(100%)'
        }}>
          {/* Panel Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8f9fa'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              Keyword Details
            </Typography>
            <IconButton onClick={handleCloseSidePanel} size="small">
              <ClearIcon />
            </IconButton>
          </Box>
          
          {/* Panel Content */}
          <Box sx={{ p: 3 }}>
            {/* Keyword Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word' }}>
                {selectedKeywordForDetails.keyword}
              </Typography>
              <Chip
                label={`Position #${selectedKeywordForDetails.position}`}
                sx={{
                  bgcolor: getPositionColorForPanel(selectedKeywordForDetails.position),
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
            
            {/* Position History Chart */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Position History
            </Typography>
            <Card sx={{ p: 2, mb: 3, height: 300 }}>
              {isLoadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>Loading position history...</Typography>
                </Box>
              ) : createPositionChartData().length > 0 ? (
                <Box sx={{ height: 240 }}>
                  <Line 
                    data={{
                      labels: createPositionChartData().map(item => {
                        const date = new Date(item.check_date);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }),
                      datasets: [
                        {
                          label: 'Position',
                          data: createPositionChartData().map(item => item.ranking),
                          borderColor: '#2196F3',
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          fill: true,
                          tension: 0.4,
                          pointBackgroundColor: '#2196F3',
                          pointBorderColor: '#ffffff',
                          pointBorderWidth: 2,
                          pointRadius: 4,
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          display: true,
                          grid: {
                            display: false
                          }
                        },
                        y: {
                          type: 'linear' as const,
                          display: true,
                          reverse: true, // Lower position numbers (better ranks) at top
                          min: 1,  // Always start at position #1
                          max: 20, // Show up to position #20 (full SEO context)
                          suggestedMin: 1,
                          suggestedMax: 20,
                          grid: {
                            color: 'rgba(0, 0, 0, 0.05)',
                            drawOnChartArea: true,
                          },
                          ticks: {
                            stepSize: 1,
                            callback: function(value: any) {
                              const pos = Math.round(value);
                              if (pos <= 3) return '🏆 #' + pos; // Gold zone
                              if (pos <= 10) return '📄 #' + pos; // Page 1
                              return '💀 #' + pos; // SEO graveyard
                            }
                          }
                        }
                      },
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context: any) {
                              const pos = context.parsed.y;
                              if (pos <= 3) return `🏆 Position #${pos} (Top 3)`;
                              if (pos <= 10) return `📄 Position #${pos} (Page 1)`;
                              return `💀 Position #${pos} (SEO Graveyard)`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  flexDirection: 'column',
                  color: 'text.secondary'
                }}>
                  <InfoIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    No position history available
                  </Typography>
                  <Typography variant="caption" sx={{ textAlign: 'center', mt: 0.5 }}>
                    Position tracking data will appear here once collected
                  </Typography>
                </Box>
              )}
            </Card>
            
            {/* Metrics Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196F3' }}>
                    {selectedKeywordForDetails.searchVolume.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Search Volume (est.)
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: selectedKeywordForDetails.estimatedVisits > 50 ? '#4CAF50' : 
                           selectedKeywordForDetails.estimatedVisits > 10 ? '#FF9800' : '#666'
                  }}>
                    {selectedKeywordForDetails.estimatedVisits}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Est. Monthly Visits
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            
            {/* Traffic Data */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Actual Performance
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                    {selectedKeywordForDetails.clicks || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Clicks
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2196F3' }}>
                    {selectedKeywordForDetails.impressions || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Impressions
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF9800' }}>
                    {selectedKeywordForDetails.ctr || 0}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    CTR
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            
            {/* URL Info */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Ranking URL
            </Typography>
            <Card sx={{ p: 2, mb: 3 }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', color: '#666' }}>
                {selectedKeywordForDetails.url || 'URL not available'}
              </Typography>
            </Card>
            
            {/* Competitors */}
            {selectedKeywordForDetails.competitors && selectedKeywordForDetails.competitors.length > 0 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Top Competitors
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {selectedKeywordForDetails.competitors.slice(0, 5).map((comp, idx) => (
                    <Box key={idx} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: idx < selectedKeywordForDetails.competitors!.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}>
                      <Typography variant="body2">
                        {comp.domain}
                      </Typography>
                      <Chip
                        label={`#${comp.position}`}
                        size="small"
                        sx={{
                          bgcolor: comp.position < selectedKeywordForDetails.position ? '#F44336' : '#4CAF50',
                          color: 'white',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </>
            )}
            
            {/* Alternative URLs */}
            {selectedKeywordForDetails.alternativeUrls && selectedKeywordForDetails.alternativeUrls.length > 1 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Alternative URLs
                </Typography>
                <Card sx={{ p: 2, mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    This keyword ranks on {selectedKeywordForDetails.alternativeUrls.length} different URLs:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {selectedKeywordForDetails.alternativeUrls.map((altUrl, idx) => (
                      <Box key={idx} sx={{ 
                        py: 1.5, 
                        px: 2,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: altUrl.url_id === selectedKeywordForDetails.urlId ? 'rgba(33, 150, 243, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                        border: altUrl.url_id === selectedKeywordForDetails.urlId ? '1px solid #2196F3' : '1px solid #e0e0e0'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Chip
                            label={`#${altUrl.position}`}
                            size="small"
                            sx={{
                              bgcolor: altUrl.position <= 3 ? '#4CAF50' : altUrl.position <= 10 ? '#FF9800' : '#F44336',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                          {altUrl.url_id === selectedKeywordForDetails.urlId && (
                            <Chip label="Current" size="small" color="primary" sx={{ fontSize: '0.7rem' }} />
                          )}
                          {altUrl.website_id !== selectedWebsiteId && (
                            <Chip label="Alt Domain" size="small" color="warning" sx={{ fontSize: '0.7rem' }} />
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                          {altUrl.url}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                          {altUrl.website_id}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </>
            )}
            
            {/* Opportunity Analysis */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Opportunity
            </Typography>
            <Card sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Current potential:</strong> ~{selectedKeywordForDetails.estimatedVisits} visits/month
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>If ranking #1:</strong> ~{Math.round(selectedKeywordForDetails.searchVolume * 0.284)} visits/month
              </Typography>
              <Typography variant="body2" color="primary">
                <strong>Upside:</strong> +{Math.round(selectedKeywordForDetails.searchVolume * 0.284 - selectedKeywordForDetails.estimatedVisits)} visits
              </Typography>
            </Card>
            
            {/* Content Updates Timeline */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, color: '#7b61ff' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Content Update Timeline
                </Typography>
              </Box>
              
              {isLoadingUpdates ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : contentUpdates.length === 0 ? (
                <Card sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    No content updates recorded for this URL yet
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  {/* Timeline line */}
                  <Box sx={{
                    position: 'absolute',
                    left: 20,
                    top: 10,
                    bottom: 10,
                    width: 2,
                    bgcolor: '#e0e0e0'
                  }} />
                  
                  {/* Timeline items */}
                  {contentUpdates.map((update: any, index: number) => {
                    const updateTypes: Record<string, { icon: string; color: string }> = {
                      major_rewrite: { icon: '📝', color: '#9c27b0' },
                      content_expansion: { icon: '📚', color: '#2196f3' },
                      technical_seo: { icon: '⚙️', color: '#ff5722' },
                      schema_update: { icon: '🏗️', color: '#ff9800' },
                      ui_refresh: { icon: '🎨', color: '#4caf50' },
                      custom: { icon: '🔧', color: '#607d8b' }
                    };
                    const updateConfig = updateTypes[update.update_type] || { icon: '🔧', color: '#607d8b' };
                    
                    const userMapping: Record<string, { name: string; icon: string }> = {
                      'newyorknederland@gmail.com': { name: 'Charlotte', icon: '🌷' },
                      'newyorkcitycontent@gmail.com': { name: 'Magali', icon: '✨' },
                      'langace@gmail.com': { name: 'Eric', icon: '🚀' },
                      'newyorkcitypublicrelations@gmail.com': { name: 'Danielle', icon: '📢' },
                      'finlandnewyork@gmail.com': { name: 'Jaana', icon: '🌲' },
                      'james@codeops.nl': { name: 'James', icon: '👨‍💻' },
                      'julienewyorkcity@gmail.com': { name: 'Margaux', icon: '🎭' },
                    };
                    const userInfo = update.created_by 
                      ? userMapping[update.created_by] || { name: update.created_by.split('@')[0], icon: '👤' } 
                      : { name: 'Unknown', icon: '👤' };
                    
                    return (
                      <Box key={update.id} sx={{ 
                        display: 'flex', 
                        mb: 3,
                        position: 'relative'
                      }}>
                        {/* Timeline dot */}
                        <Box sx={{
                          position: 'absolute',
                          left: 12,
                          top: 8,
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: updateConfig.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          zIndex: 1,
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          <span style={{ fontSize: '8px' }}>{updateConfig.icon}</span>
                        </Box>
                        
                        {/* Content */}
                        <Box sx={{ ml: 6, flex: 1 }}>
                          <Card sx={{ 
                            p: 2, 
                            bgcolor: index === 0 ? 'rgba(123, 97, 255, 0.05)' : 'white',
                            border: index === 0 ? '1px solid rgba(123, 97, 255, 0.3)' : '1px solid #e0e0e0'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ 
                                color: updateConfig.color,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}>
                                {updateConfig.icon} {update.update_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(update.update_date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {update.notes}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {userInfo.icon} {userInfo.name}
                                </Typography>
                              </Box>
                              
                              {update.impact_score && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Impact:
                                  </Typography>
                                  {[...Array(5)].map((_, i) => (
                                    <span key={i} style={{ 
                                      color: i < update.impact_score ? '#7b61ff' : '#e0e0e0',
                                      fontSize: '12px'
                                    }}>★</span>
                                  ))}
                                </Box>
                              )}
                              
                              {update.position_before && (
                                <Typography variant="caption" color="text.secondary">
                                  Position at update: #{update.position_before}
                                </Typography>
                              )}
                            </Box>
                          </Card>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Keyword Management Modal */}
      <KeywordManagementModal
        open={keywordManagementOpen}
        onClose={() => {
          setKeywordManagementOpen(false);
          setSelectedUrlIdForManagement(null);
        }}
        urlId={selectedUrlIdForManagement || ''}
        urlPath={urlFilter}
        currentKeyword={keywordFilter}
        onKeywordsUpdated={() => {
          // Refresh the keywords list
          fetchFilterOptions();
        }}
      />
      
      {/* Content Update Modal */}
      <ContentUpdateModal
        open={contentUpdateOpen}
        onClose={() => {
          setContentUpdateOpen(false);
          setSelectedUpdateData(null);
        }}
        urlId={selectedUpdateData?.urlId || ''}
        urlPath={selectedUpdateData?.urlPath || ''}
        currentPosition={selectedUpdateData?.currentPosition}
        onUpdateAdded={() => {
          // Could refresh data or show a success message
          console.log('Content update logged successfully');
        }}
      />
    </Container>
  );
};

export default RankingsPage;// Force reload Wed May 28 15:54:03 CEST 2025
